import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { Link } from 'react-router-dom';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost';
export type ButtonSize = 'sm' | 'md' | 'lg';

const BASE =
  'inline-flex items-center justify-center gap-2 rounded-btn font-medium whitespace-nowrap transition-[filter,color,background-color,border-color] disabled:cursor-not-allowed disabled:opacity-55';

const SIZES: Record<ButtonSize, string> = {
  sm: 'px-4 py-1.5 text-[0.8125rem]',
  md: 'px-6 py-2.5 text-[0.9375rem]',
  lg: 'px-7 py-3 text-base',
};

/*
 * primary 用渐变而非纯色：这是阿里云最容易辨认的一处特征。
 * 渐变端点已逐个核算过白字对比度，最低 4.77:1（见 index.css 的 :root 注释）。
 */
const VARIANTS: Record<ButtonVariant, string> = {
  primary:
    'bg-grad-cta text-grad-fg shadow-[0_6px_18px_-10px_rgb(46_95_232/0.75)] hover:brightness-110 active:brightness-95',
  secondary: 'border border-line-strong bg-surface-1 text-fg hover:border-brand hover:text-brand',
  ghost: 'px-0 text-fg underline decoration-fg-subtle decoration-1 underline-offset-4 hover:decoration-fg',
};

export function buttonClass(variant: ButtonVariant = 'primary', size: ButtonSize = 'md', extra = ''): string {
  return [BASE, VARIANTS[variant], variant === 'ghost' ? '' : SIZES[size], extra].filter(Boolean).join(' ');
}

interface CommonProps {
  variant?: ButtonVariant;
  size?: ButtonSize;
  className?: string;
  children: ReactNode;
}

export function Button({
  variant = 'primary',
  size = 'md',
  className,
  children,
  ...rest
}: CommonProps & ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button type="button" className={buttonClass(variant, size, className)} {...rest}>
      {children}
    </button>
  );
}

/** 站内跳转。 */
export function ButtonLink({
  to,
  variant = 'primary',
  size = 'md',
  className,
  children,
}: CommonProps & { to: string }) {
  return (
    <Link to={to} className={buttonClass(variant, size, className)}>
      {children}
    </Link>
  );
}

/** 站外链接（平台、GitHub），统一加 `target=_blank` 与 `rel`。 */
export function ButtonAnchor({
  href,
  variant = 'primary',
  size = 'md',
  className,
  children,
  external = false,
}: CommonProps & { href: string; external?: boolean }) {
  return (
    <a
      href={href}
      className={buttonClass(variant, size, className)}
      {...(external || /^https?:/i.test(href)
        ? { target: '_blank', rel: 'noopener noreferrer' }
        : {})}
    >
      {children}
    </a>
  );
}
